import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import SectionPanel from "pages/analytics/components/SectionPanel";
import { EmptyMessage, ErrorBanner } from "pages/analytics/components/AnalyticsStates";
import { formatNumber, formatPercent } from "pages/analytics/components/analyticsFormatters";
import { isCustomRangeIncomplete } from "utils/analyticsQueryParams";

const CHART_COLORS = [
  "#4F46E5",
  "#0D9488",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#06B6D4",
  "#F97316",
  "#10B981",
  "#EC4899",
  "#64748B",
];

const CHART_HEIGHT = 280;

const ChartSkeleton = () => (
  <div className="animate-pulse bg-gray-200 rounded" style={{ height: CHART_HEIGHT }} />
);

const BreakdownTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  const row = entry.payload ?? {};
  return (
    <div className="bg-white p-3 border border-gray-200 shadow-sm rounded-md text-sm">
      <p className="font-medium text-gray-900">{row.name}</p>
      <p style={{ color: row.fill || entry.color }}>
        {formatNumber(row.count)} contacts ({formatPercent(row.percent)})
      </p>
    </div>
  );
};

export default function UtmBreakdownPieChart({
  title = "Contact Share by Source",
  rows,
  isLoading,
  error,
  filters,
  emptyMessage = "No UTM source data for the selected period.",
  className = "",
}) {
  const chartData = (rows ?? [])
    .filter((row) => Number(row.count) > 0)
    .map((row, i) => ({
      name: row.value || "(not set)",
      value: row.count,
      count: row.count,
      percent: row.percent,
      fill: CHART_COLORS[i % CHART_COLORS.length],
    }));

  let body;
  if (isLoading) {
    body = <ChartSkeleton />;
  } else if (isCustomRangeIncomplete(filters)) {
    body = (
      <EmptyMessage message={`Select start and end dates to view ${title.toLowerCase()}.`} />
    );
  } else if (error) {
    body = (
      <>
        <div className="mb-4">
          <ErrorBanner message={error} />
        </div>
        <EmptyMessage message={`${title} unavailable due to a loading error.`} />
      </>
    );
  } else if (chartData.length === 0) {
    body = <EmptyMessage message={emptyMessage} />;
  } else {
    body = (
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="45%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={2}
          >
            {chartData.map((entry) => (
              <Cell key={entry.name} fill={entry.fill} />
            ))}
          </Pie>
          <Tooltip content={<BreakdownTooltip />} />
          <Legend
            verticalAlign="bottom"
            wrapperStyle={{
              fontSize: 12,
              maxHeight: 72,
              overflowY: "auto",
              width: "100%",
              paddingTop: 8,
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  return (
    <SectionPanel title={title} className={className}>
      {body}
    </SectionPanel>
  );
}
